import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { RootState } from "../../store/store";
import {
  Dropdown,
  DropdownOption,
  DropdownType,
} from "cprime-foundational-components";
import { createMultiSelectRenderOption } from "../Common/multiSelectRenderOption";
import * as style from "./CreateEditRolesDetails.module.scss";
import {
  getRoleSetupDrodownData,
  getRoleDetailsByRoleId,
} from "../../services/RequestAccess/RequestAccess";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

export interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: DropdownOption | DropdownOption[] | null | string;
  regionMode?: "Regional" | "National";
  logicalOperator?: string;
}

interface RoleConditionsFiltersProps {
  filters: FilterCondition[];
  setRoleConditionsPreview: any;
  onFiltersChange: (updatedFilters: FilterCondition[]) => void;
  onRoleConditionsChange?: (roleConditions: any[]) => void;
  isRoleCloning?: boolean;
}

const operatorOptions: DropdownOption[] = [
  { label: "equals", value: "equals", id: "equals" },
  { label: "is not equal", value: "notEqual", id: "notEqual" },
];

const logicalOperatorOptions: DropdownOption[] = [
  { label: "AND", value: "AND", id: "AND" },
  { label: "OR", value: "OR", id: "OR" },
];

const RoleConditionsFilters: React.FC<RoleConditionsFiltersProps> = ({
  filters: initialFilters,
  onFiltersChange,
  onRoleConditionsChange,
  setRoleConditionsPreview,
  isRoleCloning,
}) => {
  const scssClass = style as any;
  const { id, roleId, action } = useParams();
  const applicationId = id?.split("-")[1];
  const role_id = roleId?.split("-")[1];
  const isEditMode = action === "edit-role" && !!role_id;
  const cloneRoleData = useSelector(
    (state: RootState) => state?.createRole?.cloneRoleData,
  );

  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<string[]>([]);
  const [optionsMap, setOptionsMap] = useState<
    Record<string, DropdownOption[]>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  // const [globalLogicalOperator, setGlobalLogicalOperator] =
  //   useState<string>("AND");

  const formatFieldName = (key: string): string => {
    switch (key) {
      case "regions":
        return "Region";
      case "fleets":
        return "Fleet";
      case "dealers":
        return "Dealer Name";
      case "distributions":
        return "Distribution";
      case "plants":
        return "Plant";
      case "lifecycleStatuses":
        return "Lifecycle Status";
      default:
        return key
          ?.replace(/_/g, " ")
          ?.replace(/\b\w/g, (c) => c?.toUpperCase());
    }
  };

  useEffect(() => {
    if (!applicationId) return;

    const fetchDropdownData = async () => {
      try {
        const response = await getRoleSetupDrodownData(applicationId);
        if (response?.status === "SUCCESS" && response?.data) {
          const data = response?.data;
          const map: Record<string, DropdownOption[]> = {};

          Object.entries(data)?.forEach(([key, items]: [string, any]) => {
            const formattedName = formatFieldName(key);

            const cleanedItems = (items || [])?.filter((item: any) => {
              const labelKey = Object.keys(item)?.find((k) =>
                k?.endsWith("_name"),
              );
              const labelValue = labelKey ? item[labelKey] : null;
              return labelValue && String(labelValue)?.trim() !== "";
            });

            map[formattedName] = cleanedItems?.map((item: any) => {
              const labelKey = Object.keys(item)?.find((k) =>
                k?.endsWith("_name"),
              );
              const labelValue =
                labelKey && typeof item[labelKey] === "string"
                  ? item[labelKey]
                  : String(item.id);

              return {
                id: item?.id?.toString(),
                label: labelValue,
                value: item?.id?.toString(),
              };
            });
          });

          setOptionsMap(map);
          setFieldOptions(Object.keys(map));
        }
      } catch (error) {
        // console.error("Error fetching dropdown data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDropdownData();
  }, [applicationId]);

  useEffect(() => {
    const fetchRoleDetailsIfEdit = async () => {
      const id = isRoleCloning ? cloneRoleData?.sourceRoleId : role_id;
      if (
        (isEditMode || isRoleCloning) &&
        id &&
        fieldOptions?.length > 0 &&
        Object.keys(optionsMap)?.length > 0
      ) {
        try {
          const response = await getRoleDetailsByRoleId(id);
          if (
            response?.status === "SUCCESS" &&
            response?.data?.roleConditions?.length > 0
          ) {
            const roleConditions = response?.data?.roleConditions;

            const mappedFilters: FilterCondition[] = roleConditions?.map(
              (cond: any) => {
                const { fieldName, operator, values, andOrOperator } = cond;

                const uiOperator =
                  operator === "equal"
                    ? "equals"
                    : operator === "not equal"
                      ? "notEqual"
                      : operator;

                const isRegionField =
                  fieldName === "Regional" || fieldName === "National";

                // use optionsMap["Region"] instead of optionsMap["Regional"]
                const options = isRegionField
                  ? optionsMap["Region"] || []
                  : optionsMap[fieldName] || [];

                const selectedValues = options?.filter((opt) =>
                  values?.includes(opt.id),
                );

                return {
                  id: cond.id?.toString(),
                  field: isRegionField ? "Region" : fieldName,
                  operator: uiOperator,
                  value: selectedValues,
                  regionMode: isRegionField ? fieldName : undefined,
                  logicalOperator: andOrOperator || null,
                };
              },
            );

            setFilters(mappedFilters);
            setRoleConditionsPreview(mappedFilters);
            onFiltersChange(mappedFilters);
            setInitialized(true);
          }
        } catch (error) {
          // console.error("Error fetching role details:", error);
        }
      }
    };

    fetchRoleDetailsIfEdit();
  }, [isEditMode, role_id, fieldOptions, optionsMap, onFiltersChange]);

  // Initialize filters (create a default if not edit mode)
  useEffect(() => {
    if (!initialized && !isLoading && fieldOptions?.length > 0 && !isEditMode) {
      if (initialFilters && initialFilters?.length > 0) {
        setFilters(initialFilters);
        onFiltersChange(initialFilters);
      } else {
        const defaultField = fieldOptions[0];
        const newFilter: FilterCondition = {
          id: Date.now()?.toString(),
          field: defaultField,
          operator: "equals",
          value: defaultField === "Region" ? [] : optionsMap[defaultField]?.[0],
          regionMode: defaultField === "Region" ? "Regional" : undefined,
          logicalOperator: "AND",
        };
        setFilters([newFilter]);
        onFiltersChange([newFilter]);
      }
      setInitialized(true);
    }
  }, [
    fieldOptions,
    optionsMap,
    initialFilters,
    initialized,
    isLoading,
    isEditMode,
    onFiltersChange,
  ]);

  // Sync filters to parent
  const syncFilters = (updated: FilterCondition[]) => {
    setFilters(updated);
    onFiltersChange(updated);

    if (onRoleConditionsChange) {
      const structured = updated.map((f, idx) => {
        const seq = idx + 1;
        return {
          fieldName: f.field === "Region" ? f.regionMode : f.field,
          operator:
            f.operator === "equals"
              ? "equal"
              : f.operator === "notEqual"
                ? "not equal"
                : f.operator,
          values:
            f.field === "Region" && f.regionMode === "National"
              ? []
              : Array.isArray(f.value)
                ? f.value.map((v) => String(v.id))
                : f.value
                  ? [String(f.value)]
                  : [],
          andOrOperator: seq === 1 ? null : f.logicalOperator,
          sequenceNo: seq,
        };
      });
      onRoleConditionsChange(structured);
    }
  };

  const updateFilter = (
    index: number,
    key: keyof FilterCondition,
    value: any,
  ) => {
    let updated = [...filters];

    // Handle FIELD change
    if (key === "field") {
      const newField = value.label;
      updated[index].field = newField;

      if (newField === "Region") {
        updated[index].regionMode = "Regional";
        updated[index].value = [];
      } else {
        delete updated[index].regionMode;
        updated[index].value = [];
      }
    }

    // Region mode selection
    else if (key === "regionMode") {
      updated[index].regionMode = value;
      if (value === "National") {
        updated[index].value = "";
      }
    }
    // LOGICAL OPERATOR handling (the new logic)
    // else if (key === "logicalOperator") {
    //   const chosen = value.value;
    //   // Set global operator
    //   // setGlobalLogicalOperator(chosen);
    //   // Apply chosen AND/OR to ALL rows except the first
    //   updated = updated.map((f, idx) => ({
    //     ...f,
    //     logicalOperator: idx === 0 ? null : chosen,
    //   }));
    // }
    // Operator = equals / notEqual
    else if (key === "operator") {
      updated[index].operator = value.value;
    }
    // Normal value change
    else if (key === "value") {
      updated[index].value = value;
    }
    syncFilters(updated);
  };

  const getAvailableFieldOptions = (currentField: string): DropdownOption[] => {
    const selectedFields = filters
      ?.map((f) => f.field)
      ?.filter((f) => f !== currentField);
    return fieldOptions
      ?.filter((field) => !selectedFields?.includes(field))
      ?.map((field) => ({ id: field, label: field, value: field }));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...filters];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered?.splice(result.destination.index, 0, moved);
    syncFilters(reordered);
  };

  const addCondition = () => {
    const nextField = fieldOptions.find(
      (field) => !filters.some((f) => f.field === field),
    );
    if (!nextField) return;

    // default to last row's operator if exists, otherwise AND
    const lastLogicalOperator =
      filters?.length > 0
        ? filters[filters?.length - 1].logicalOperator || "AND"
        : "AND";

    const newFilter: FilterCondition = {
      id: Date.now().toString(),
      field: nextField,
      operator: "equals",
      value: null,
      regionMode: nextField === "Region" ? "Regional" : undefined,
      logicalOperator: filters?.length === 0 ? undefined : lastLogicalOperator,
    };
    syncFilters([...filters, newFilter]);
  };

  const removeCondition = (id: string) => {
    syncFilters(filters?.filter((f) => f.id !== id));
  };

  if (isLoading) {
    return <div>Loading Additional Filters ...</div>;
  }

  return (
    <div className={scssClass.roleConditionsContainer}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="filters">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {filters?.map((filter, index) => (
                <Draggable
                  key={filter.id}
                  draggableId={filter.id}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={scssClass.roleConditionsConditionRow}
                    >
                      {/* <div
                        style={{
                          minWidth: index === 0 ? 85 : 95,
                          maxWidth: index === 0 ? 85 : 95,
                          fontWeight: 600,
                          marginLeft: index === 0 ? "12px" : "",
                          marginRight: "15px",
                        }}
                      >
                        {index === 0 ? (
                          " Where"
                        ) : (
                          <Dropdown
                            label=""
                            options={logicalOperatorOptions}
                            selectedOptions={[
                              {
                                id: filter.logicalOperator || "AND",
                                label: filter.logicalOperator || "AND",
                                value: filter.logicalOperator || "AND",
                              },
                            ]}
                            disabled={index !== 1}
                            onChange={(selected) => {
                              if (index !== 1) return;
                              const chosen = String(selected[0].value);
                              const updated = filters.map((f, idx) => ({
                                ...f,
                                logicalOperator: idx === 0 ? undefined : chosen,
                              }));
                              syncFilters(updated);
                            }}
                            dropdownSx={{
                              width: "6rem",
                            }}
                            style={{
                              pointerEvents: index === 1 ? "auto" : "none",
                            }}
                          />
                        )}
                      </div> */}

                      <div
                        className={scssClass.roleConditionsDropdownsContainer}
                      >
                        <div>
                          <Dropdown
                            label=""
                            options={getAvailableFieldOptions(filter.field)}
                            selectedOptions={[
                              {
                                id: filter.field,
                                label: filter.field,
                                value: filter.field,
                              },
                            ]}
                            onChange={(selected) =>
                              updateFilter(index, "field", selected[0])
                            }
                          />
                        </div>

                        <div
                          style={{
                            maxWidth: "18rem",
                          }}
                        >
                          <Dropdown
                            label=""
                            options={operatorOptions}
                            selectedOptions={[
                              operatorOptions?.find(
                                (op) => op.value === filter.operator,
                              ) || operatorOptions[0],
                            ]}
                            onChange={(selected) =>
                              updateFilter(index, "operator", selected[0])
                            }
                            dropdownSx={{
                              width: "18rem",
                            }}
                          />
                        </div>

                        {filter.field === "Region" && (
                          <div>
                            <Dropdown
                              label=""
                              options={[
                                {
                                  id: "Regional",
                                  label: "Regional",
                                  value: "Regional",
                                },
                                {
                                  id: "National",
                                  label: "National",
                                  value: "National",
                                },
                              ]}
                              selectedOptions={[
                                {
                                  id: filter.regionMode || "Regional",
                                  label: filter.regionMode || "Regional",
                                  value: filter.regionMode || "Regional",
                                },
                              ]}
                              onChange={(selected) =>
                                updateFilter(
                                  index,
                                  "regionMode",
                                  selected[0].value,
                                )
                              }
                            />
                          </div>
                        )}

                        {/* Existing Region List dropdown (visible only when Regional) */}
                        {(filter.field !== "Region" ||
                          filter.regionMode === "Regional") && (
                          <div>
                            <Dropdown
                              label=""
                              options={optionsMap[filter.field] || []}
                              selectedOptions={
                                Array.isArray(filter.value)
                                  ? filter.value
                                  : filter.value &&
                                      typeof filter.value === "object"
                                    ? [filter.value]
                                    : []
                              }
                              onChange={(selected) =>
                                updateFilter(index, "value", selected)
                              }
                              type={DropdownType.MultiSelect}
                              disableCloseOnSelect
                              renderOption={createMultiSelectRenderOption(
                                Array.isArray(filter.value)
                                  ? filter.value
                                  : filter.value &&
                                      typeof filter.value === "object"
                                    ? [filter.value]
                                    : [],
                                (selected) =>
                                  updateFilter(index, "value", selected),
                              )}
                              errorMessage=""
                              error={
                                !filter?.value ||
                                (Array.isArray(filter?.value) &&
                                  filter?.value?.length === 0)
                              }
                            />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          justifyContent: "center",
                          marginLeft: "20px",
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                        <button
                          onClick={() => removeCondition(filter.id)}
                          data-testid="remove-filter-button"
                          className={scssClass.roleConditionsRemoveButton}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className={scssClass.roleConditionsAddButtonContainer}>
        <button
          onClick={addCondition}
          className={scssClass.roleConditionsAddButton}
          disabled={filters?.length >= fieldOptions?.length}
          style={{
            color: filters?.length >= fieldOptions?.length ? "#ccc" : "#000",
            cursor:
              filters?.length >= fieldOptions?.length
                ? "not-allowed"
                : "pointer",
          }}
        >
          Add condition +
        </button>
      </div>
    </div>
  );
};

export default RoleConditionsFilters;
